import { useDataProvider, useNotify, type Identifier } from "ra-core";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { fullName, type GulaClient } from "./gulaTypes";
import { Field, SimpleSelect } from "./SimpleSelect";
import { agentName, useAgents } from "./useAgents";

const todayIso = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
};

/** New task for any client, assigned to one of the team. */
export const NewTaskDialog = ({
  open,
  onClose,
  onCreated,
  clients = [],
  preset,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  /** Clients to pick from (when no preset) */
  clients?: GulaClient[];
  /** Fixed client (task created from the client panel) */
  preset?: { contactId: Identifier; name: string };
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { taskTypes } = useConfigurationContext();
  const { workers, meId } = useAgents();
  const [search, setSearch] = useState("");
  const [contact, setContact] = useState<{
    id: Identifier;
    name: string;
  } | null>(null);
  const [type, setType] = useState("call");
  const [text, setText] = useState("");
  const [date, setDate] = useState(todayIso());
  const [time, setTime] = useState("10:00");
  const [agentId, setAgentId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setContact(preset ? { id: preset.contactId, name: preset.name } : null);
    setType("call");
    setText("");
    setDate(todayIso());
    setTime("10:00");
    setAgentId(meId == null ? "" : String(meId));
  }, [open, preset, meId]);

  const suggestions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle.length < 2) return [];
    return clients
      .filter((client) =>
        [fullName(client), client.phone, client.email]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(needle)),
      )
      .slice(0, 6);
  }, [clients, search]);

  const handleCreate = async () => {
    if (!contact || !text.trim() || !agentId) {
      notify("Elige el cliente, escribe la tarea y a quién se asigna", {
        type: "warning",
      });
      return;
    }
    setSaving(true);
    try {
      await dataProvider.create("tasks", {
        data: {
          contact_id: contact.id,
          type,
          text: text.trim(),
          due_date: new Date(`${date}T${time || "10:00"}`).toISOString(),
          sales_id: Number(agentId),
        },
      });
      notify("Tarea creada", { type: "success" });
      onCreated();
    } catch (error) {
      console.error(error);
      notify("No se ha podido crear la tarea", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nueva tarea</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {preset ? null : (
            <Field label="Cliente">
              {contact ? (
                <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                  {contact.name}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setContact(null)}
                  >
                    Cambiar
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <Input
                    autoFocus
                    placeholder="Busca por nombre, teléfono o email"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                  {suggestions.map((client) => (
                    <button
                      key={client.id}
                      type="button"
                      className="rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
                      onClick={() =>
                        setContact({
                          id: client.contact_id,
                          name: fullName(client),
                        })
                      }
                    >
                      {fullName(client)}
                      <span className="ml-2 text-xs text-muted-foreground">
                        {client.phone ?? client.email}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </Field>
          )}
          <Field label="Qué hay que hacer">
            <Textarea
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="Ej.: llamar para cerrar la fecha de instalación"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <SimpleSelect
              label="Tipo"
              value={type}
              onChange={setType}
              options={taskTypes}
            />
            <SimpleSelect
              label="Para"
              value={agentId}
              onChange={setAgentId}
              options={workers.map((agent) => ({
                value: String(agent.id),
                label: agentName(agent),
              }))}
            />
            <Field label="Día">
              <Input
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
              />
            </Field>
            <Field label="Hora">
              <Input
                type="time"
                value={time}
                onChange={(event) => setTime(event.target.value)}
              />
            </Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleCreate} disabled={saving}>
            {saving ? "Creando…" : "Crear tarea"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
